import type React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Loader2, MinusCircle, XCircle, X } from 'lucide-react';
import type {
  BulkGroupAction,
  BulkGroupResultStatus,
  IBulkGroupActionResult,
  IGroupData,
} from '@tool-no-hu/shared/types';

const VERB: Record<BulkGroupAction, string> = { start: 'Start', stop: 'Stop' };

const GAME_BADGE_COLORS: Record<string, string> = {
  TX: 'bg-[#3b82f6]/20 text-[#3b82f6]',
  XD: 'bg-[#8b5cf6]/20 text-[#8b5cf6]',
  TL: 'bg-[#f59e0b]/20 text-[#f59e0b]',
};

/** Khung overlay dùng chung cho 2 modal — theo pattern modal của sun. */
function Overlay({
  title,
  onClose,
  busy,
  children,
  footer,
}: {
  title: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <motion.div
        className="max-h-[85vh] w-full max-w-md overflow-hidden rounded-2xl border border-[#252a38] bg-[#161920] shadow-2xl"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#252a38] px-4 py-3">
          <div className="text-sm font-bold text-[#e2e8f0]">{title}</div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Đóng"
            className="grid h-7 w-7 place-items-center rounded-md text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t border-[#252a38] px-4 py-3">
          {footer}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Sheet xác nhận trước khi chạy ──────────────────────────────────────────

export interface SkippedGroup {
  group: IGroupData;
  reason: string;
}

interface BulkActionSheetProps {
  open: boolean;
  action: BulkGroupAction;
  /** Nhóm ĐỦ điều kiện — sẽ chạy. */
  run: IGroupData[];
  /** Nhóm bị bỏ qua kèm lý do. */
  skip: SkippedGroup[];
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Bảng xác nhận trước khi chạy bulk: chia rõ "sẽ chạy" và "bỏ qua (kèm lý do)".
 * Bỏ qua là CẢNH BÁO, không phải lỗi. Nút chạy tự tắt khi không có nhóm nào đủ
 * điều kiện.
 */
export function BulkActionSheet({
  open,
  action,
  run,
  skip,
  busy,
  onConfirm,
  onClose,
}: BulkActionSheetProps) {
  const verb = VERB[action];
  return (
    <AnimatePresence>
      {open && (
        <Overlay
          title={`${verb} ${String(run.length + skip.length)} nhóm đã chọn`}
          onClose={onClose}
          busy={busy}
          footer={
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-lg px-4 py-2 text-[13px] font-semibold text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-50"
              >
                Huỷ
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy || run.length === 0}
                className="flex items-center gap-2 rounded-lg bg-[#3b82f6] px-4 py-2 text-[13px] font-semibold text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {run.length > 0 ? `${verb} ${String(run.length)} nhóm` : verb}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <section className="space-y-1.5">
              <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[#4ade80]">
                <CheckCircle2 className="h-4 w-4" /> Sẽ {verb.toLowerCase()}
                <span className="ml-auto font-mono">{run.length}</span>
              </h4>
              {run.length > 0 ? (
                <ul className="space-y-1">
                  {run.map((group) => (
                    <GroupRow key={String(group._id)} group={group} tone="ok" trailing="sẵn sàng" />
                  ))}
                </ul>
              ) : (
                <div className="flex items-start gap-2 rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-[12px] text-[#f59e0b]">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Không có nhóm nào đủ điều kiện {verb.toLowerCase()}. Chọn nhóm khác hoặc đổi
                    hành động.
                  </span>
                </div>
              )}
            </section>

            {skip.length > 0 && (
              <section className="space-y-1.5">
                <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[#f59e0b]">
                  <AlertTriangle className="h-4 w-4" /> Bỏ qua
                  <span className="ml-auto font-mono">{skip.length}</span>
                </h4>
                <ul className="space-y-1">
                  {skip.map(({ group, reason }) => (
                    <GroupRow key={String(group._id)} group={group} tone="skip" trailing={reason} />
                  ))}
                </ul>
              </section>
            )}
          </div>
        </Overlay>
      )}
    </AnimatePresence>
  );
}

function GroupRow({
  group,
  tone,
  trailing,
}: {
  group: IGroupData;
  tone: 'ok' | 'skip';
  trailing: string;
}) {
  const toneClass = tone === 'ok' ? 'bg-[#22c55e]/10' : 'bg-[#f59e0b]/10';
  const trailClass = tone === 'ok' ? 'text-[#4ade80]' : 'text-[#f59e0b]';
  return (
    <li className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] ${toneClass}`}>
      <span className="font-semibold text-[#e2e8f0]">{group.name}</span>
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
          GAME_BADGE_COLORS[group.gameType] ?? 'bg-[#1e2235] text-[#64748b]'
        }`}
      >
        {group.gameType}
      </span>
      <span className={`ml-auto text-[12px] ${trailClass}`}>{trailing}</span>
    </li>
  );
}

// ─── Modal kết quả sau khi chạy ─────────────────────────────────────────────

const STATUS_META: Record<
  BulkGroupResultStatus,
  { label: string; icon: typeof CheckCircle2; row: string; text: string }
> = {
  ok: { label: 'Thành công', icon: CheckCircle2, row: 'bg-[#22c55e]/10', text: 'text-[#4ade80]' },
  skipped: { label: 'Bỏ qua', icon: MinusCircle, row: 'bg-[#f59e0b]/10', text: 'text-[#f59e0b]' },
  error: { label: 'Lỗi', icon: XCircle, row: 'bg-[#ef4444]/10', text: 'text-[#ef4444]' },
};

/** Thứ tự hiển thị: thành công trước, rồi bỏ qua, lỗi cuối để dễ soi. */
const ORDER: BulkGroupResultStatus[] = ['ok', 'skipped', 'error'];

interface BulkResultModalProps {
  /** `null` khi đóng. */
  data: { action: BulkGroupAction; results: IBulkGroupActionResult[] } | null;
  onClose: () => void;
}

/**
 * Bảng kết quả sau khi chạy bulk — từng nhóm một, gom theo trạng thái. Server
 * luôn trả kèm mảng này kể cả khi mọi nhóm bị bỏ qua, nên đây là chỗ DUY NHẤT
 * nói được nhóm nào chạy, nhóm nào bị bỏ (vì sao), nhóm nào hỏng.
 */
export function BulkResultModal({ data, onClose }: BulkResultModalProps) {
  const counts = (status: BulkGroupResultStatus): number =>
    (data?.results ?? []).filter((r) => r.status === status).length;

  const sorted = [...(data?.results ?? [])].sort(
    (a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status),
  );

  return (
    <AnimatePresence>
      {data && (
        <Overlay
          title={
            <span className="flex items-center gap-2">
              {VERB[data.action]} hàng loạt
              <span className="text-[12px] font-normal text-[#64748b]">
                {counts('ok')} chạy · {counts('skipped')} bỏ qua · {counts('error')} lỗi
              </span>
            </span>
          }
          onClose={onClose}
          footer={
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-[#3b82f6] px-4 py-2 text-[13px] font-semibold text-white transition-all hover:brightness-110"
            >
              Xong
            </button>
          }
        >
          <ul className="space-y-1">
            {sorted.map((result) => {
              const meta = STATUS_META[result.status];
              const Icon = meta.icon;
              return (
                <li
                  key={result.groupId}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] ${meta.row}`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${meta.text}`} />
                  <span className="font-semibold text-[#e2e8f0]">{result.name}</span>
                  <span className={`ml-auto text-right text-[12px] ${meta.text}`}>
                    {result.reason ?? meta.label}
                  </span>
                </li>
              );
            })}
          </ul>

          {counts('error') > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#ef4444]/40 bg-[#ef4444]/10 p-3 text-[12px] text-[#ef4444]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Nhóm lỗi giữ nguyên trạng thái cũ — thử lại từng nhóm để xem chi tiết.</span>
            </div>
          )}
        </Overlay>
      )}
    </AnimatePresence>
  );
}
